import React, { useState, useMemo } from 'react';
import { Habit, CheckInLog } from '../types';
import { AVAILABLE_ICONS } from '../constants';
import {
  format, subMonths, eachDayOfInterval,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addMonths, isSameMonth, isSameDay, parseISO
} from 'date-fns';
import {
  ArrowLeft, CalendarDays, Flame, Check, FileText, Image as ImageIcon,
  Clock, Calendar, ChevronLeft, ChevronRight, MessageCircle
} from 'lucide-react';
import { CheckInImage } from './CheckInImage';

interface ArchivedHabitDetailViewProps {
  habit: Habit;
  onBack: () => void;
  onRestore: (id: string) => void;
  onEdit: (habit: Habit) => void;
}

export const ArchivedHabitDetailView: React.FC<ArchivedHabitDetailViewProps> = ({
  habit, onBack, onRestore, onEdit
}) => {
  const IconComponent = AVAILABLE_ICONS.find(i => i.name === habit.icon)?.component || AVAILABLE_ICONS[0].component;
  const totalDays = habit.completedDates.length;
  const logs = habit.logs || [];
  const logsByDate: Record<string, CheckInLog> = {};
  logs.forEach(l => { logsByDate[l.date] = l; });

  const defaultCalendarDate = useMemo(() => {
    if (habit.endDate) return new Date(habit.endDate);
    if (habit.createdAt) return new Date(habit.createdAt);
    return new Date();
  }, []);

  const [calendarDate, setCalendarDate] = useState(defaultCalendarDate);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'detail' | 'logs'>('detail');

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(calendarDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(calendarDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [calendarDate]);

  const weekDays = ['一', '二', '三', '四', '五', '六', '日'];

  const prevMonth = () => setCalendarDate(d => subMonths(d, 1));
  const nextMonth = () => setCalendarDate(d => addMonths(d, 1));

  const startDate = habit.startDate ? format(new Date(habit.startDate), 'yyyy-MM-dd') : habit.createdAt ? format(new Date(habit.createdAt), 'yyyy-MM-dd') : '';
  const endDate = habit.endDate ? format(new Date(habit.endDate), 'yyyy-MM-dd') : '';

  const getFrequencyLabel = () => {
    switch (habit.frequency) {
      case 'daily': return '每天';
      case 'weekly': return '每周';
      case 'interval': return `每 ${habit.intervalDays || 2} 天`;
      default: return '每天';
    }
  };

  const completionRate = (() => {
    if (!habit.startDate || !habit.endDate) return 0;
    const start = new Date(habit.startDate);
    const end = new Date(habit.endDate);
    const totalDaysInRange = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (totalDaysInRange === 0) return 0;

    // Calculate expected check-in days based on frequency
    let expectedDays = totalDaysInRange;
    if (habit.frequency === 'weekly' && habit.targetDays && habit.targetDays.length > 0) {
      expectedDays = 0;
      const days = eachDayOfInterval({ start, end });
      days.forEach(d => {
        if (habit.targetDays.includes(d.getDay())) expectedDays++;
      });
    } else if (habit.frequency === 'interval') {
      const interval = habit.intervalDays || 2;
      expectedDays = Math.ceil(totalDaysInRange / interval);
    }

    if (expectedDays === 0) return 0;
    return Math.round((totalDays / expectedDays) * 100);
  })();

  const maxStreak = useMemo(() => {
    if (habit.completedDates.length === 0) return 0;

    const sorted = [...habit.completedDates].sort();
    let max = 1;
    let current = 1;

    if (habit.frequency === 'daily' || habit.frequency === 'workdays') {
      for (let i = 1; i < sorted.length; i++) {
        const prev = new Date(sorted[i - 1]);
        const curr = new Date(sorted[i]);
        const diff = Math.round((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
        if (diff === 1) {
          current++;
          max = Math.max(max, current);
        } else {
          current = 1;
        }
      }
    } else if (habit.frequency === 'weekly') {
      // Count consecutive completed weeks
      const weekSet = new Set(sorted);
      const targetDays = habit.targetDays || [];
      if (targetDays.length === 0) return 0;

      // Find first and last week
      const firstDate = new Date(sorted[0]);
      const lastDate = new Date(sorted[sorted.length - 1]);

      let weeks = 0;
      let currentWeekStart = new Date(firstDate);
      currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());

      const lastWeekStart = new Date(lastDate);
      lastWeekStart.setDate(lastWeekStart.getDate() - lastWeekStart.getDay());

      while (currentWeekStart <= lastWeekStart) {
        let weekComplete = true;
        for (let i = 0; i < 7; i++) {
          const d = new Date(currentWeekStart);
          d.setDate(d.getDate() + i);
          if (targetDays.includes(d.getDay())) {
            if (!weekSet.has(format(d, 'yyyy-MM-dd'))) {
              weekComplete = false;
              break;
            }
          }
        }
        if (weekComplete) {
          weeks++;
          max = Math.max(max, weeks);
        } else {
          weeks = 0;
        }
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }
    } else if (habit.frequency === 'interval') {
      const interval = habit.intervalDays || 2;
      for (let i = 1; i < sorted.length; i++) {
        const prev = new Date(sorted[i - 1]);
        const curr = new Date(sorted[i]);
        const diff = Math.round((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
        if (diff === interval) {
          current++;
          max = Math.max(max, current);
        } else {
          current = 1;
        }
      }
    }

    return max;
  }, [habit.completedDates, habit.frequency, habit.targetDays, habit.intervalDays]);

  const selectedLog = selectedDate ? logsByDate[selectedDate] : null;
  const isSelectedDateCompleted = selectedDate ? habit.completedDates.includes(selectedDate) : false;

  if (viewMode === 'logs') {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setViewMode('detail')}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600 dark:text-slate-300" />
          </button>
          <div className="flex items-center gap-2">
            <MessageCircle size={20} className="text-indigo-500" />
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">心得记录 ({logs.length})</h1>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">暂无心得记录</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {logs.sort((a, b) => b.date.localeCompare(a.date)).map((log) => {
                const date = parseISO(log.date);
                const month = format(date, 'MMM');
                const day = format(date, 'd');
                const weekday = format(date, 'EEE');

                return (
                  <div key={log.id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 text-center">
                        <p className="text-xs text-slate-400">{month}</p>
                        <p className="text-lg font-bold text-slate-800 dark:text-white">{day}</p>
                        <p className="text-xs text-slate-400">{weekday}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        {log.note && (
                          <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{log.note}</p>
                        )}
                        {log.image && (
                          <div className="mt-2">
                            <div className="w-28 h-28 rounded-lg overflow-hidden">
                              <CheckInImage imagePath={log.image} alt="打卡图片" />
                            </div>
                          </div>
                        )}
                        <div className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                          <Clock size={12} />
                          {format(new Date(log.timestamp), 'HH:mm')}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
        >
          <ArrowLeft size={20} className="text-slate-600 dark:text-slate-300" />
        </button>
        <h1 className="text-xl font-bold text-slate-800 dark:text-white">归档详情</h1>
      </div>

      {/* Habit Summary Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-sm border border-slate-100 dark:border-slate-700">
        <div className="flex items-start gap-4">
          <div className={`p-4 rounded-xl ${habit.color} text-white shadow-lg`}>
            <IconComponent size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-800 dark:text-white text-xl">{habit.title}</h2>
            {habit.description && (
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{habit.description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-400">累计打卡</p>
            <p className="text-2xl font-bold text-slate-800 dark:text-white mt-1">{totalDays} <span className="text-sm font-normal text-slate-400">天</span></p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-400">最高连续</p>
            <p className="text-2xl font-bold text-orange-500 mt-1">{maxStreak} <span className="text-sm font-normal text-slate-400">{habit.frequency === 'weekly' ? '周' : '天'}</span></p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-400">完成率</p>
            <p className="text-2xl font-bold text-green-500 mt-1">{completionRate}%</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-400">心得记录</p>
            <button
              onClick={() => setViewMode('logs')}
              className="text-2xl font-bold text-indigo-500 mt-1 hover:text-indigo-600 transition-colors cursor-pointer"
            >
              {logs.length} <span className="text-sm font-normal text-slate-400">条</span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4 text-xs text-slate-400">
          <span className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-lg">
            <Calendar size={12} /> {getFrequencyLabel()}
          </span>
          {startDate && (
            <span className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-lg">
              <CalendarDays size={12} /> {startDate} ~ {endDate}
            </span>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onEdit(habit)}
            className="flex-1 py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            编辑
          </button>
          <button
            onClick={() => onRestore(habit.id)}
            className="flex-1 py-2 px-3 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 text-sm font-medium hover:bg-cyan-200 dark:hover:bg-cyan-900/50 transition-colors flex items-center justify-center gap-1"
          >
            <ArrowLeft size={14} /> 恢复
          </button>
        </div>
      </div>

      {/* Monthly Ring Calendar */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="p-4 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center space-x-2 text-slate-800 dark:text-white font-bold">
            <Calendar size={20} className="text-primary-500" />
            <span>{format(calendarDate, 'yyyy年 MM月')}</span>
          </div>
          <div className="flex space-x-1">
            <button onClick={prevMonth} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
              <ChevronLeft size={20} />
            </button>
            <button onClick={nextMonth} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-7 mb-2">
            {weekDays.map(d => (
              <div key={d} className="text-center text-xs text-slate-400 font-medium py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-2 gap-x-1">
            {calendarDays.map((day) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const isCurrentMonth = isSameMonth(day, calendarDate);
              const isToday = isSameDay(day, new Date());
              const isCompleted = habit.completedDates.includes(dateStr);
              const isSelected = selectedDate === dateStr;

              return (
                <button
                  key={dateStr}
                  onClick={() => setSelectedDate(isCompleted ? dateStr : null)}
                  className={`
                    relative aspect-square flex items-center justify-center rounded-xl transition-all
                    ${!isCurrentMonth ? 'opacity-30' : ''}
                    ${isSelected ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20' : ''}
                    ${isToday ? 'bg-slate-50 dark:bg-slate-700/50' : ''}
                    ${isCompleted && !isSelected ? 'hover:bg-slate-50 dark:hover:bg-slate-700/30' : ''}
                    ${!isCompleted && !isSelected ? 'cursor-default' : 'cursor-pointer'}
                  `}
                >
                  <span className={`text-xs z-10 font-medium ${
                    isSelected ? 'text-primary-600 dark:text-primary-400 font-bold' :
                    isToday ? 'text-primary-600 dark:text-primary-400 font-bold' :
                    'text-slate-600 dark:text-slate-400'
                  }`}>
                    {format(day, 'd')}
                  </span>

                  {isCompleted && (
                    <div className="absolute inset-0 pointer-events-none">
                      <svg viewBox="0 0 36 36" className="w-full h-full rotate-[-90deg]">
                        <circle
                          cx="18"
                          cy="18"
                          r="17"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`${habit.color.replace('bg-', 'text-')} opacity-80`}
                        />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Selected Date Detail */}
      {selectedDate && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-fade-in">
          <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check size={16} className="text-green-500" />
              <h3 className="font-bold text-slate-800 dark:text-white">
                {format(parseISO(selectedDate), 'yyyy年MM月dd日')}
              </h3>
            </div>
            <button
              onClick={() => setSelectedDate(null)}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div className="p-4">
            {isSelectedDateCompleted ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <div className={`w-3 h-3 rounded-full ${habit.color}`} />
                  <span className="font-medium">已完成打卡</span>
                </div>

                {selectedLog?.note && (
                  <div className="flex items-start gap-2">
                    <FileText size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-slate-400 mb-1">心得</p>
                      <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{selectedLog.note}</p>
                    </div>
                  </div>
                )}

                {selectedLog?.image && (
                  <div className="flex items-start gap-2">
                    <ImageIcon size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-slate-400 mb-1">打卡图片</p>
                      <div className="w-32 h-32 rounded-lg overflow-hidden">
                        <CheckInImage imagePath={selectedLog.image} alt="打卡图片" />
                      </div>
                    </div>
                  </div>
                )}

                {selectedLog && (
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <Clock size={12} />
                    打卡时间: {format(new Date(selectedLog.timestamp), 'HH:mm:ss')}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">该日未打卡</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
